/* eslint-disable react-refresh/only-export-components */
import type { PresentationData } from '../../types/presentation'
import {
  EnhancedContentSlide,
  EnhancedGridSlide,
  FeatureCard,
} from '../../components/presentation'
import { Card } from '../../components/ui/card'

/**
 * KI-Beratung Erstgespräch Presentation
 *
 * Initial consultation presentation for AI consulting and RAG systems.
 * Updated with homepage colors and professional styling.
 */

// ============================================
// CUSTOM COMPONENTS FOR THIS PRESENTATION
// ============================================

interface GeometricTitleSlideProps {
  title: string
  subtitle?: string
  author1?: { name: string; role: string }
  author2?: { name: string; role: string }
  valueProp1?: { text: string }
  valueProp2?: { text: string }
  valueProp3?: { text: string }
  sessionDuration?: string
  sessionConfidentiality?: string
  sessionAgenda?: string
}

function GeometricTitleSlide({
  subtitle,
  author1,
  author2,
  valueProp1,
  valueProp2,
  valueProp3,
  sessionDuration,
  sessionConfidentiality,
  sessionAgenda,
}: GeometricTitleSlideProps) {
  return (
  <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-600 dark:from-orange-600 dark:via-red-600 dark:to-yellow-500">
    {/* Animated Background Pattern */}
    <div className="absolute inset-0 overflow-hidden opacity-20">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>
    </div>

    {/* Gradient Overlay */}
    <div className="absolute inset-0 bg-gradient-radial from-white/10 via-transparent to-transparent" />

    {/* Content Grid */}
    <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-16 h-full px-8 lg:px-20 py-16 pb-24 items-center">
      {/* Left Column */}
      <div className="space-y-6 lg:space-y-8">
        {/* Company Badge */}
        <div className="inline-flex items-center gap-3 bg-white/20 border border-white/30 px-6 py-3 rounded-full text-white font-medium backdrop-blur-sm">
          <span>AI Expertise</span>
        </div>

        {/* Main Title */}
        <h1 className="text-5xl lg:text-7xl font-light text-white leading-tight">
          <span className="font-bold">KI</span>
          <span className="text-white/70 font-extralight">-</span>
          <span className="font-normal">Beratung</span>
          <br />
          <span className="font-extralight italic text-white/80 text-4xl lg:text-6xl">&</span>{' '}
          <span className="font-normal">Entwicklung</span>
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <h2 className="text-2xl lg:text-3xl font-light text-white/90 leading-relaxed">
            <span className="font-medium">Erstgespräch:</span>
            <br />
            {subtitle}
          </h2>
        )}

        {/* Value Props */}
        <div className="flex flex-wrap gap-4 lg:gap-6">
          {valueProp1 && (
            <div className="flex items-center gap-3 bg-white/15 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/25">
              <span>{valueProp1.text}</span>
            </div>
          )}
          {valueProp2 && (
            <div className="flex items-center gap-3 bg-white/15 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/25">
              <span>{valueProp2.text}</span>
            </div>
          )}
          {valueProp3 && (
            <div className="flex items-center gap-3 bg-white/15 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/25">
              <span>{valueProp3.text}</span>
            </div>
          )}
        </div>
      </div>

      {/* Right Column */}
      <div className="space-y-6 lg:space-y-8">
        {/* Presenter Card */}
        {(author1 || author2) && (
          <Card className="bg-white/15 backdrop-blur-md border-white/25 text-white">
            <div className="p-6 lg:p-8">
              <h3 className="text-xl lg:text-2xl font-semibold text-white mb-6">Ihre Experten</h3>
              <div className="space-y-6">
                {author1 && (
                  <div className="flex items-center gap-4">
                    <img
                      src="/images/jan.png"
                      alt={author1.name}
                      className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                    />
                    <div>
                      <h4 className="text-lg font-semibold">{author1.name}</h4>
                      <p className="text-white/90 text-sm font-medium">{author1.role}</p>
                    </div>
                  </div>
                )}
                {author2 && (
                  <div className="flex items-center gap-4">
                    <img
                      src="/images/kai.png"
                      alt={author2.name}
                      className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                    />
                    <div>
                      <h4 className="text-lg font-semibold">{author2.name}</h4>
                      <p className="text-white/90 text-sm font-medium">{author2.role}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Session Details */}
        {(sessionDuration || sessionConfidentiality || sessionAgenda) && (
          <Card className="bg-black/20 backdrop-blur-md border-white/15 text-white">
            <div className="p-6 lg:p-8 space-y-4">
              {sessionDuration && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Dauer</div>
                    <div className="font-medium text-white">{sessionDuration}</div>
                  </div>
                </div>
              )}
              {sessionConfidentiality && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Vertraulichkeit</div>
                    <div className="font-medium text-white">{sessionConfidentiality}</div>
                  </div>
                </div>
              )}
              {sessionAgenda && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Agenda</div>
                    <div className="font-medium text-white">{sessionAgenda}</div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>

    <style>{`
      .bg-gradient-radial {
        background: radial-gradient(ellipse at top right, rgba(255, 255, 255, 0.15) 0%, transparent 50%);
      }
    `}</style>
  </div>
  )
}

// Numbered Benefit Card with clean styling
interface NumberedBenefitCardProps {
  number: string
  title: string
  description: string
}

function NumberedBenefitCard({ number, title, description }: NumberedBenefitCardProps) {
  return (
  <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:-translate-y-1 transition-transform duration-300 shadow-lg">
    <div className="p-6">
      <div className="text-5xl font-bold text-blue-600 dark:text-orange-500 mb-4">{number}</div>
      <h4 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{title}</h4>
      <p className="text-gray-600 dark:text-gray-300 font-medium">{description}</p>
    </div>
  </Card>
  )
}

// Architecture Layer Component
interface ArchLayerProps {
  title: string
  description: string
  gradient: string
}

function ArchLayer({ title, description, gradient }: ArchLayerProps) {
  return (
    <div className={`p-4 lg:p-5 rounded-2xl text-white shadow-lg hover:translate-x-2 transition-transform ${gradient}`}>
      <h4 className="text-lg lg:text-xl font-semibold mb-1">
        {title}
      </h4>
      <p className="text-white/90 text-sm lg:text-base">{description}</p>
    </div>
  )
}

// ============================================
// PRESENTATION DATA
// ============================================

export const erstgespraech: PresentationData = {
  metadata: {
    title: 'KI-Beratung Erstgespräch',
    subtitle: 'Strukturiertes Erstgespräch für KI-Beratung und RAG-Systeme',
    author: 'Jan Pfrommer & Kai Bennett',
    date: '2025',
    duration: 90,
    tags: ['AI', 'Consulting', 'RAG', 'Business'],
    description: 'Initial consultation presentation for AI advisory services, covering client needs assessment, AI maturity, and solution options.',
  },
  slides: [
    // Slide 1: Custom Title Slide
    {
      id: 1,
      component: () => (
        <GeometricTitleSlide
          title="KI-Beratung & Entwicklung"
          subtitle="Ihr Weg zur erfolgreichen KI-Integration"
          author1={{ name: 'Jan Pfrommer', role: 'AI Strategy & Implementation' }}
          author2={{ name: 'Kai Bennett', role: 'Software Architect' }}
          valueProp1={{ text: 'Strategische Beratung' }}
          valueProp2={{ text: 'RAG-Systeme' }}
          valueProp3={{ text: 'Praxisnahe Schulungen' }}
          sessionDuration="60-90 Minuten"
          sessionConfidentiality="Vollständig garantiert"
          sessionAgenda="Strukturiert & effizient"
        />
      ),
      transition: 'fade',
    },

    // Slide 2: Ziel des Gesprächs
    {
      id: 2,
      component: () => (
        <EnhancedContentSlide
          title="Ziel unseres Gesprächs"
          subtitle="Was wir heute gemeinsam erreichen möchten"
          variant="light"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <NumberedBenefitCard
              number="01"
              title="Verständnis Ihrer Situation"
              description="Ihre aktuellen Herausforderungen und Pain Points kennenlernen"
            />
            <NumberedBenefitCard
              number="02"
              title="AI-Reifegrad einschätzen"
              description="Technische und organisatorische Voraussetzungen bewerten"
            />
            <NumberedBenefitCard
              number="03"
              title="Passende Lösungen finden"
              description="Beratung, Schulung oder Entwicklung - was macht für Sie Sinn?"
            />
            <NumberedBenefitCard
              number="04"
              title="Machbarkeit konkret zeigen"
              description="Live-Demo unseres RAG-Systems - keine Theorie, sondern funktionierende Technologie"
            />
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 3: Agenda
    {
      id: 3,
      component: () => (
        <EnhancedContentSlide
          title="Gesprächsablauf"
          subtitle="60-90 Minuten strukturiert und effizient"
          variant="light"
        >
          <div className="flex flex-col gap-2 mt-4 max-w-4xl mx-auto pb-24">
            <ArchLayer
              title="1. Einleitung (5 min)"
              description="Vorstellung • Ziele klären • Rahmenbedingungen"
              gradient="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600"
            />
            <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>
            <ArchLayer
              title="2. Kundenkontext (15-20 min)"
              description="Prozesse • KI-Erfahrung • Datenquellen • Anforderungen"
              gradient="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-red-600 dark:to-yellow-600"
            />
            <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>
            <ArchLayer
              title="3. Demo & Impulse (15-25 min)"
              description="RAG-System live • Best Practices • Potenziale aufzeigen"
              gradient="bg-gradient-to-r from-purple-600 to-pink-600 dark:from-yellow-600 dark:to-orange-600"
            />
            <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>
            <ArchLayer
              title="4. Leistungsbausteine (10-15 min)"
              description="Modulare Angebote • Passende Lösungen finden"
              gradient="bg-gradient-to-r from-pink-600 to-rose-600 dark:from-orange-600 dark:to-red-600"
            />
            <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>
            <ArchLayer
              title="5. Next Steps (5-10 min)"
              description="Zusammenfassung • Pilotprojekt • Konkrete Schritte"
              gradient="bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-green-600 dark:to-emerald-600"
            />
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 4: Kai Bennett (Presenter Slide)
    {
      id: 4,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-700 to-slate-900 dark:from-gray-800 dark:to-gray-950 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-6 pb-24">
            <div className="max-w-6xl text-white w-full">
              {/* Header with Image and Name */}
              <div className="flex items-center gap-6 mb-6">
                <img
                  src="/images/kai.png"
                  alt="Kai Bennett"
                  className="w-28 h-28 rounded-2xl object-cover shadow-2xl flex-shrink-0"
                />
                <div className="text-left">
                  <h2 className="text-4xl font-bold">Kai Bennett</h2>
                  <p className="text-xl font-medium text-white/90 mt-1">
                    <strong>Software Architect</strong>
                  </p>
                  <p className="text-base text-white/80 mt-1">
                    10+ Jahre Erfahrung in .NET-Entwicklung • KI & RAG-Systeme seit 2023 • Certified Software Architect (iSAQB CPSA-F CPSA-A)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column: Education & Certifications */}
                <div className="space-y-4">
                  {/* Education */}
                  <div className="p-4 bg-white/10 rounded-xl border border-white/20">
                    <h4 className="text-white mb-2 text-sm font-semibold">
                      Ausbildung
                    </h4>
                    <div className="space-y-2 text-xs">
                      <div className="text-white/90">
                        <strong>B.Sc. Angewandte Informatik</strong><br />
                        <span className="text-white/70">FHDW | 2013–2015</span>
                      </div>
                      <div className="text-white/90">
                        <strong>IT Assistent</strong><br />
                        <span className="text-white/70">Lippe Berufskolleg | 2010–2013</span>
                      </div>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="p-4 bg-white/10 rounded-xl border border-white/20">
                    <h4 className="text-white mb-3 text-sm font-semibold">
                      Zertifizierungen
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <img
                          src="https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png"
                          alt="iSAQB CPSA-F"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                        />
                        <p className="text-xs text-white/90">
                          <strong>CPSA-F</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img
                          src="https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png"
                          alt="iSAQB CPSA-A"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                        />
                        <p className="text-xs text-white/90">
                          <strong>CPSA-A</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img
                          src="https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png"
                          alt="iSAQB CPSA-F CPSA-A"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1 text-white text-xs font-bold" style={{ display: 'none' }}>
                          CPSA
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>CPSA-F/A</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Skills Grid */}
                <div className="grid grid-cols-1 gap-3">
                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        AI & Infrastructure
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Open-Source LLMs • Document Intelligence</li>
                        <li>• AI Search • Container Apps</li>
                        <li>• Functions & Service Bus • SQL Databases</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Development
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Python (FastAPI, RAG) • C# & .NET</li>
                        <li>• Qdrant Vector DB • Docker & Kubernetes</li>
                        <li>• REST APIs • Microservices</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Architektur & DevOps
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Software-Architektur • Infrastruktur</li>
                        <li>• DevOps • CI/CD Pipelines</li>
                        <li>• Dapr & Service Mesh • IaC</li>
                      </ul>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 5: Jan Pfrommer (Presenter Slide)
    {
      id: 5,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-indigo-700 to-purple-900 dark:from-gray-800 dark:to-gray-950 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-6 pb-24">
            <div className="max-w-6xl text-white w-full">
              {/* Header with Image and Name */}
              <div className="flex items-center gap-6 mb-6">
                <img
                  src="/images/jan.png"
                  alt="Jan Pfrommer"
                  className="w-28 h-28 rounded-2xl object-cover shadow-2xl flex-shrink-0"
                />
                <div className="text-left">
                  <h2 className="text-4xl font-bold">Jan Pfrommer</h2>
                  <p className="text-xl font-medium text-white/90 mt-1">
                    <strong>Software Engineer & AI Engineer</strong>
                  </p>
                  <p className="text-base text-white/80 mt-1">
                    Frontend, Backend & Requirements Engineering • Scrum Master & KI-Projekte
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column: Education & Certifications */}
                <div className="space-y-4">
                  {/* Education */}
                  <div className="p-4 bg-white/10 rounded-xl border border-white/20">
                    <h4 className="text-white mb-2 text-sm font-semibold">
                      Ausbildung
                    </h4>
                    <div className="space-y-2 text-xs">
                      <div className="text-white/90">
                        <strong>M.Sc. Information Systems</strong><br />
                        <span className="text-white/70">HS Pforzheim | 2017–2019</span>
                      </div>
                      <div className="text-white/90">
                        <strong>B.Sc. Wirtschaftsinformatik</strong><br />
                        <span className="text-white/70">HS Pforzheim | 2012–2017</span>
                      </div>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="p-4 bg-white/10 rounded-xl border border-white/20">
                    <h4 className="text-white mb-3 text-sm font-semibold">
                      Zertifizierungen
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <img
                          src="https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg"
                          alt="CPRE-FL"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1 text-white text-xs font-bold" style={{ display: 'none' }}>
                          CPRE
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>CPRE-FL</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img
                          src="https://logodix.com/logo/649849.jpg"
                          alt="ISTQB CTFL"
                          className="w-16 h-16 mb-1 mx-auto object-contain bg-white/10 rounded-lg"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1 text-white text-xs font-bold" style={{ display: 'none' }}>
                          ISTQB
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>ISTQB</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img
                          src="https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png"
                          alt="SAP BTP Developer"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1 text-white text-xs font-bold" style={{ display: 'none' }}>
                          SAP
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>SAP BTP</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Skills Grid */}
                <div className="grid grid-cols-1 gap-3">
                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        AI & Context Engineering
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Context Engineering • AI Engineering</li>
                        <li>• Prompt Engineering • RAG-Architektur</li>
                        <li>• GitHub Copilot • Model Context Protocol</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Development
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• TypeScript/JavaScript • Python • Java</li>
                        <li>• React • Vue.js • Flutter • Angular</li>
                        <li>• Spring Boot • FastAPI • SAP BTP</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Requirements & Agile
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Requirements Engineering • Testing</li>
                        <li>• Scrum Master • UX/UI Design</li>
                        <li>• Prozessoptimierung • Consulting</li>
                      </ul>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 6: Unser Ansatz
    {
      id: 6,
      component: () => (
        <EnhancedGridSlide
          title="Unser Ansatz"
          subtitle="Wie wir Sie zum Erfolg führen"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              title="Enterprise-Ready"
              description="Skalierbare On-Premise-Architekturen. Production-Ready von Anfang an."
              variant="default"
            />,
            <FeatureCard
              title="KI-Praxis seit 2023"
              description="Tägliche Arbeit mit Open-Source LLMs, RAG-Systemen & AI-Integration."
              variant="default"
            />,
            <FeatureCard
              title="Privacy-First"
              description="On-Premise oder Cloud - DSGVO-konform und datenschutzsicher."
              variant="default"
            />,
            <FeatureCard
              title="Agile & Ganzheitlich"
              description="Von Requirements über Development bis Deployment. Scrum, DevOps, CI/CD."
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 7: Kundenkontext - Fragen
    {
      id: 7,
      component: () => (
        <EnhancedGridSlide
          title="Ihre Situation verstehen"
          subtitle="Wichtige Fragen für eine präzise Einschätzung"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              title="Prozesse & Aufgaben"
              description="Welche Aufgaben oder Prozesse möchten Sie optimieren oder automatisieren?"
              items={['Use Cases identifizieren', 'Potenziale bewerten']}
              variant="default"
            />,
            <FeatureCard
              title="KI-Erfahrung"
              description="Gibt es bereits Berührungspunkte mit KI? (Copilot, ChatGPT, Open-Source LLMs)"
              items={['Reifegrad bestimmen', 'Erfahrungen nutzen']}
              variant="default"
            />,
            <FeatureCard
              title="Datenquellen"
              description="Welche Daten stehen zur Verfügung? (Dokumente, Datenbanken, Wissenssysteme)"
              items={['Qualität prüfen', 'Zugriff sichern']}
              variant="default"
            />,
            <FeatureCard
              title="Compliance & Datenschutz"
              description="Welche Anforderungen bestehen? Ist On-Prem oder Cloud-Lösung möglich?"
              items={['DSGVO-Konformität', 'Security Standards']}
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 8: KI-Reifegrad
    {
      id: 8,
      component: () => (
        <EnhancedContentSlide
          title="KI-Reifegrad bestimmen"
          subtitle="Wo stehen Sie aktuell?"
          variant="light"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-8">
            <Card className="flex-1 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-center p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🌱</div>
              <h4 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Neugierig</h4>
              <p className="text-gray-600 dark:text-gray-300 mb-2 font-medium">Erste Schritte</p>
              <small className="text-gray-500 dark:text-gray-400">"Was kann KI?"</small>
            </Card>

            <div className="text-4xl text-blue-600 dark:text-orange-500 font-bold">→</div>

            <Card className="flex-1 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-center p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🔬</div>
              <h4 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Experimentell</h4>
              <p className="text-gray-600 dark:text-gray-300 mb-2 font-medium">Ausprobieren</p>
              <small className="text-gray-500 dark:text-gray-400">ChatGPT, Copilot</small>
            </Card>

            <div className="text-4xl text-blue-600 dark:text-orange-500 font-bold">→</div>

            <Card className="flex-1 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-center p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🚀</div>
              <h4 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Pilotphase</h4>
              <p className="text-gray-600 dark:text-gray-300 mb-2 font-medium">Erste Projekte</p>
              <small className="text-gray-500 dark:text-gray-400">Internes Testing</small>
            </Card>

            <div className="text-4xl text-blue-600 dark:text-orange-500 font-bold">→</div>

            <Card className="flex-1 bg-gradient-to-br from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 text-center p-6 shadow-lg">
              <div className="text-4xl mb-4">✅</div>
              <h4 className="text-xl font-semibold mb-2 text-white">Produktiv</h4>
              <p className="text-white/90 mb-2 font-medium">Live-Betrieb</p>
              <small className="text-white font-semibold">Production-Ready</small>
            </Card>
          </div>

          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 mt-8">
            <div className="p-6 text-center">
              <p className="text-lg text-white font-medium">
                <strong>Wichtig:</strong> Jede Phase hat ihre eigenen Anforderungen.
                Wir holen Sie genau dort ab, wo Sie stehen.
              </p>
            </div>
          </Card>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 9: Demo-Ankündigung
    {
      id: 9,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-600 dark:from-orange-600 dark:via-red-600 dark:to-yellow-500 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-8 text-center">
            <div className="max-w-4xl space-y-12 text-white">
              <div className="text-6xl lg:text-7xl font-bold drop-shadow-lg">
                🚀
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold drop-shadow-lg">
                Live-Demo
              </h1>
              <h2 className="text-3xl lg:text-4xl font-light drop-shadow-md">
                Unser Enterprise RAG-System in Aktion
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                Sehen Sie, wie Ihr Team interne Wissensdaten<br />
                schneller und präziser abrufen kann
              </p>
              <Card className="bg-white/20 border-white/30 backdrop-blur-md text-white mx-auto max-w-2xl">
                <div className="p-8 text-center">
                  <div className="text-4xl mb-6">✅</div>
                  <h4 className="text-2xl font-semibold mb-4">Standard-LLM vs. RAG-LLM</h4>
                  <p className="text-white/90 text-lg leading-relaxed">
                    Sehen Sie den Unterschied: Allgemeine Antworten vs. kontextualisierte,
                    faktenbasierte Antworten mit Quellenangaben
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 10: Leistungsbausteine
    {
      id: 10,
      component: () => (
        <EnhancedGridSlide
          title="Modulare Leistungsbausteine"
          subtitle="Flexible Lösungen für Ihre Anforderungen"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              title="AI-Strategie & Schulung"
              description="Mitarbeiter fit machen, eigene Use-Cases finden"
              items={[
                'Developer-Workshops',
                'Prompt-Engineering-Schulungen',
                'Best-Practice-Sessions',
              ]}
              variant="default"
            />,
            <FeatureCard
              title="RAG-System & Wissensplattform"
              description="Eigene Daten sicher mit LLMs nutzbar machen"
              items={[
                'On-Prem oder Cloud (Open-Source LLMs)',
                'Qdrant Vector Database',
                'FastAPI Backend',
              ]}
              variant="default"
            />,
            <FeatureCard
              title="KI-Integration in Systeme"
              description="Effizienzsteigerung durch Automatisierung"
              items={[
                'Chatbot-Integration',
                'Agent-Workflows',
                'API-Entwicklung',
              ]}
              variant="default"
            />,
            <FeatureCard
              title="Entwicklung & Support"
              description="Maßgeschneiderte Lösungen"
              items={[
                'Rapid Prototyping',
                'Custom Development',
                'Deployment & Operations',
              ]}
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 11: Total Cost of Ownership (NEUE FOLIE)
    {
      id: 11,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
          <div className="relative z-10 p-6 lg:p-12 h-full flex flex-col justify-center pb-24">
            <div className="max-w-6xl mx-auto w-full">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                Total Cost of Ownership
              </h2>
              <h3 className="text-xl lg:text-2xl text-gray-600 dark:text-gray-400 mb-12 text-center">
                Cloud API vs. Lokale Inferenz (12 Monate)
              </h3>

              {/* TCO Table */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 text-white">
                      <th className="px-6 py-4 text-left font-semibold">Szenario</th>
                      <th className="px-6 py-4 text-center font-semibold">Cloud API (GPT-4o)</th>
                      <th className="px-6 py-4 text-center font-semibold">Lokal (RTX 5090)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-6 py-5 font-medium text-gray-900 dark:text-white">500K tokens/Tag</td>
                      <td className="px-6 py-5 text-center text-gray-600 dark:text-gray-300">~$6.000 – $9.000</td>
                      <td className="px-6 py-5 text-center font-semibold text-green-600 dark:text-green-400">~$2.500</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                      <td className="px-6 py-5 font-medium text-gray-900 dark:text-white">1M tokens/Tag</td>
                      <td className="px-6 py-5 text-center text-gray-600 dark:text-gray-300">~$12.000 – $18.000</td>
                      <td className="px-6 py-5 text-center font-semibold text-green-600 dark:text-green-400">~$2.500</td>
                    </tr>
                    <tr className="bg-gray-50 dark:bg-gray-800/50">
                      <td className="px-6 py-5 font-medium text-gray-900 dark:text-white">5M tokens/Tag</td>
                      <td className="px-6 py-5 text-center text-gray-600 dark:text-gray-300">~$60.000 – $90.000</td>
                      <td className="px-6 py-5 text-center font-semibold text-green-600 dark:text-green-400">~$5.000</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Key Insight Card */}
              <Card className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 text-white border-0">
                <div className="p-6 text-center">
                  <p className="text-lg leading-relaxed">
                    <strong>Break-even point:</strong> Ein einzelner RTX 5090 amortisiert sich typischerweise zwischen
                    <span className="text-yellow-200 dark:text-yellow-300 font-bold"> 1-3 Monaten </span>
                    moderater API-Nutzung. Danach läuft lokale Inferenz auf Stromkosten.
                    <span className="block mt-2 text-white/80 italic">Bei hohen Nutzungsvolumen ist die Abrechnung nicht mal mehr naheliegend.</span>
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 12: Ihr Schreibtisch ist das neue Rechenzentrum (NEUE FOLIE)
    {
      id: 12,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, black 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 p-6 lg:p-12 h-full flex flex-col justify-center pb-24">
            <div className="max-w-5xl mx-auto w-full">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                Ihr Schreibtisch ist das neue Rechenzentrum
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-12 text-center leading-relaxed">
                Der Wandel zu lokalen LLMs ist eine strukturelle Veränderung in der Art und Weise, wie Entwickler und Organisationen mit KI interagieren
              </p>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Then Card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="text-2xl font-bold text-gray-400 dark:text-gray-500 mb-4">2024</h4>
                  <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>Dreitägiges Projekt mit tiefem System-Know-how erforderlich</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>Komplexe Setup- und Konfigurationsarbeit nötig</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>Begrenzte Modell-Optionen</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>Steile Lernkurve</span>
                    </li>
                  </ul>
                </div>

                {/* Now Card */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 rounded-2xl p-8 shadow-lg text-white">
                  <h4 className="text-2xl font-bold mb-4">2025</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-green-300 mt-1">✓</span>
                      <span><strong>10-Minuten-Setup</strong> mit modernen Tools</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-300 mt-1">✓</span>
                      <span>Modelle sind <strong>gut genug</strong> für die Produktion</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-300 mt-1">✓</span>
                      <span>Hardware ist <strong>erschwinglich genug</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-300 mt-1">✓</span>
                      <span>Tools sind <strong>einfach genug</strong></span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Call to Action */}
              <Card className="bg-white dark:bg-gray-800 border-2 border-blue-600 dark:border-orange-500">
                <div className="p-8 text-center">
                  <p className="text-xl text-gray-900 dark:text-white leading-relaxed mb-4">
                    <strong className="text-blue-600 dark:text-orange-500">Starten Sie heute:</strong> Laden Sie ein Modell mit Ollama herunter, führen Sie es aus, nutzen Sie die lokale API aus Ihrem Anwendungscode.
                  </p>
                  <p className="text-lg text-gray-600 dark:text-gray-400 italic">
                    Sobald Sie die Geschwindigkeit, die Privatsphäre und die Freiheit von Token-basiierter Abrechnung erlebt haben,
                    hört die Frage auf <span className="text-gray-400 dark:text-gray-500">"Soll ich Modelle lokal ausführen?"</span> zu sein und wird zu
                    <span className="text-blue-600 dark:text-orange-500 font-semibold"> "Warum sollte ich diese Daten woanders hin senden?"</span>
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 13: Best Practices Impulse
    {
      id: 13,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
          <div className="relative z-10 p-8 lg:p-16 h-full flex flex-col justify-center pb-24">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 text-center">Quick Wins</h2>
            <h3 className="text-xl lg:text-2xl text-gray-600 dark:text-gray-400 mb-12 text-center">Sofort umsetzbare Best Practices</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="p-8">
                  <div className="text-4xl mb-4 text-center">🧠</div>
                  <h4 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white text-center">Context-Engineering</h4>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-center font-medium">
                    Das neue Prompt-Engineering - bis zu 70% bessere Ergebnisse durch strukturierte Kontexte
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg text-sm space-y-4">
                    <div>
                      <strong className="text-gray-900 dark:text-white text-base">Context &gt; Prompts:</strong>
                      <div className="mt-2 space-y-2">
                        <div className="text-gray-600 dark:text-gray-400"><span className="text-red-500 font-semibold">✗ "Schreib einen Text"</span></div>
                        <div className="pl-4 border-l-4 border-green-500 space-y-1">
                          <div className="text-gray-700 dark:text-gray-300">✅ <strong className="text-green-600 dark:text-green-400">Kontext:</strong> "Unternehmenskontext, Zielgruppe CTOs, Fokus Business Value"</div>
                          <div className="text-gray-700 dark:text-gray-300">✅ <strong className="text-green-600 dark:text-green-400">Rolle:</strong> "Technischer Redakteur mit 10 Jahren Erfahrung"</div>
                          <div className="text-gray-700 dark:text-gray-300">✅ <strong className="text-green-600 dark:text-green-400">Aufgabe:</strong> "200-Wort-Text über RAG-Vorteile"</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="p-8">
                  <div className="text-4xl mb-4 text-center">📊</div>
                  <h4 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white text-center">RAG vs. Standard-LLM</h4>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-center font-medium">
                    Warum RAG für Unternehmensdaten?
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg text-sm space-y-4">
                    <div>
                      <strong className="text-gray-900 dark:text-white text-base">Standard-LLM:</strong>
                      <ul className="mt-2 space-y-1 text-red-500 font-medium">
                        <li>• Veraltetes Wissen</li>
                        <li>• Halluzinationen</li>
                        <li>• Keine Quellen</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-green-600 dark:text-green-400 text-base">RAG-System:</strong>
                      <ul className="mt-2 space-y-1 text-green-600 dark:text-green-400 font-medium">
                        <li>• Aktuelle Daten</li>
                        <li>• Faktenbasiert</li>
                        <li>• Quellenangaben</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 14: Typische Use Cases
    {
      id: 14,
      component: () => (
        <EnhancedGridSlide
          title="Typische Use Cases"
          subtitle="Wo KI echten Mehrwert bringt"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              title="Wissensmanagement"
              description="Interne Dokumentation durchsuchbar machen. Onboarding beschleunigen."
              variant="default"
            />,
            <FeatureCard
              title="Customer Support"
              description="24/7 Chatbot mit Zugriff auf Support-Datenbank und FAQs."
              variant="default"
            />,
            <FeatureCard
              title="Vertragsanalyse"
              description="Automatische Extraktion relevanter Klauseln aus Verträgen."
              variant="default"
            />,
            <FeatureCard
              title="Code-Assistenz"
              description="Entwickler-Support mit interner Codebasis und Best Practices."
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 15: Next Steps
    {
      id: 15,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
          <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-center pb-28">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 text-center">Nächste Schritte</h2>
            <h3 className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-8 text-center">Wie geht es weiter?</h3>

            <div className="flex flex-col gap-2 max-w-4xl mx-auto">
              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">1</span>
                    Heute
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Wir fassen zusammen: Welche Bereiche haben das größte Potenzial?
                  </p>
                </div>
              </Card>

              <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">2</span>
                    Option: Workshop
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Analyse-Session (2-4h) • Use-Cases identifizieren • Roadmap entwickeln
                  </p>
                </div>
              </Card>

              <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">3</span>
                    Option: Pilotprojekt
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    RAG-Prototyp mit Ihren Daten • Machbarkeit beweisen • Quick Win
                  </p>
                </div>
              </Card>

              <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">4</span>
                    Option: Schulung
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Developer-Workshop (1-3 Tage) • Hands-on Training • Eigene Projekte starten
                  </p>
                </div>
              </Card>

              <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">5</span>
                    Option: Video-Kurs
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Flexibles Remote-Learning • Selbstbestimmtes Tempo • Praxisnahe Inhalte & Beispiele
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 16: Kontakt
    {
      id: 16,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-600 dark:from-orange-600 dark:via-red-600 dark:to-yellow-500 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-8 text-center pb-24">
            <div className="max-w-5xl space-y-8 text-white">
              <h2 className="text-5xl lg:text-6xl font-bold">Vielen Dank!</h2>
              <h3 className="text-3xl lg:text-4xl font-light">Ihre Fragen & unser Kontakt</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                <Card className="bg-white/15 backdrop-blur-md border-white/25 text-white">
                  <div className="p-8 text-center space-y-4">
                    <img
                      src="/images/jan.png"
                      alt="Jan Pfrommer"
                      className="w-24 h-24 rounded-2xl object-cover mx-auto"
                    />
                    <h4 className="text-2xl font-semibold">Jan Pfrommer</h4>
                    <p className="flex items-center justify-center gap-2 text-white/90">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      jan.pfrommer@securesight.ai
                    </p>
                  </div>
                </Card>

                <Card className="bg-white/15 backdrop-blur-md border-white/25 text-white">
                  <div className="p-8 text-center space-y-4">
                    <img
                      src="/images/kai.png"
                      alt="Kai Bennett"
                      className="w-24 h-24 rounded-2xl object-cover mx-auto"
                    />
                    <h4 className="text-2xl font-semibold">Kai Bennett</h4>
                    <p className="flex items-center justify-center gap-2 text-white/90">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      kai.bennett@securesight.ai
                    </p>
                  </div>
                </Card>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center mt-8">
                <button className="px-8 py-4 bg-white text-blue-600 dark:text-orange-600 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors shadow-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Follow-up vereinbaren
                </button>
                <button className="px-8 py-4 bg-transparent text-white border-2 border-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-white/10 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Präsentation als PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },
  ],
}

export default erstgespraech
